/**
 * Work Orders Page.
 *
 * Lists all work orders with status and priority filtering.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Plus, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useWorkOrders } from "@/features/work-orders/api";
import { WO_STATUSES } from "@/types/work-order";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import BulkActionBar from "@/components/ui/BulkActionBar";
import BulkResultPanel from "@/components/BulkResultPanel";
import { useBulkRun, type BulkOutcome } from "@/features/bulk/api";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import ConfirmDeleteDialog from "@/components/ui/ConfirmDeleteDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  Open: "info",
  "In Progress": "warning",
  "On Hold": "default",
  Completed: "success",
  Cancelled: "danger",
};
const priorityVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  low: "default",
  medium: "info",
  high: "warning",
  urgent: "danger",
};

function WorkOrdersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { data, isLoading } = useWorkOrders({ page, status: statusFilter });

  // One request, and the server reports every row. The loop this replaces fired
  // one `DELETE ?force=true` per selected row and discarded every failure, so a
  // run that half worked said "37 of 50 deleted" and left the user to find out
  // which thirteen by looking at the list. `cascade` is no longer implied:
  // work orders with related records come back named, in the panel.
  const bulkRun = useBulkRun();
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null);

  const runBulk = (operation: string, params: Record<string, unknown> = {}) => {
    const ids = [...selected];
    bulkRun.mutate(
      { operation, selection: { ids }, params },
      {
        onSuccess: (outcome) => {
          qc.invalidateQueries({ queryKey: ["work-orders"] });
          setSelected(new Set());
          setBulkOutcome(outcome.failed > 0 ? outcome : null);
          if (outcome.failed === 0) toast.success(outcome.message);
          else if (outcome.succeeded === 0) toast.error(outcome.message);
          else toast.warning(outcome.message);
        },
        onError: (error: unknown) => {
          const detail =
            (error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? "That could not be done.";
          toast.error(detail);
        },
      },
    );
  };

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Work Orders
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Track repairs, maintenance, and refurbishment jobs.
          </p>
        </div>
        <Button onClick={() => navigate("/work-orders/new")}>
          <Plus className="h-4 w-4" /> New Work Order
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            setStatusFilter(undefined);
            setPage(1);
          }}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${!statusFilter ? "bg-blue-100 text-[var(--color-interactive-primary)]" : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]/80"}`}
        >
          All
        </button>
        {WO_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${statusFilter === s ? "bg-blue-100 text-[var(--color-interactive-primary)]" : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]/80"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : data && data.items.length > 0 ? (
        <div className="space-y-2 stagger-children">
          {data.items.map((wo) => (
            <Card
              key={wo.id}
              padding="none"
              className="cursor-pointer hover-lift"
              onClick={() => navigate(`/work-orders/${wo.id}`)}
            >
              <div className="flex items-stretch">
                <div
                  className="flex items-center pl-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(wo.id)}
                    onChange={() => toggleSelect(wo.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0 rounded border-[var(--border-default)]"
                  />
                </div>
                {/* Priority indicator bar */}
                <div
                  className={`w-1 shrink-0 rounded-l-[var(--radius-xl)] ${wo.priority === "urgent" ? "bg-red-500" : wo.priority === "high" ? "bg-amber-500" : wo.priority === "medium" ? "bg-blue-500" : "bg-gray-300"}`}
                />

                <div className="flex-1 p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-brand-600">
                      {wo.wo_number}
                    </span>
                    <Badge variant={statusVariant[wo.status] || "default"}>
                      {wo.status}
                    </Badge>
                    <Badge variant={priorityVariant[wo.priority] || "default"}>
                      {wo.priority}
                    </Badge>
                    <span className="rounded-[var(--radius-full)] bg-[var(--color-surface-tertiary)] px-2 py-0.5 text-[10px] capitalize text-[var(--text-tertiary)]">
                      {wo.work_type}
                    </span>
                  </div>
                  <h3 className="mt-1 text-sm font-semibold text-[var(--text-primary)]">
                    {wo.title}
                  </h3>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
                    {wo.actual_hours > 0 && (
                      <span>
                        {wo.actual_hours}h logged
                        {wo.estimated_hours
                          ? ` / ${wo.estimated_hours}h est.`
                          : ""}
                      </span>
                    )}
                    {wo.due_date && (
                      <span>
                        Due {new Date(wo.due_date).toLocaleDateString()}
                      </span>
                    )}
                    {wo.started_at && (
                      <span>
                        Started {new Date(wo.started_at).toLocaleDateString()}
                      </span>
                    )}
                    {wo.completed_at && (
                      <span>
                        Completed{" "}
                        {new Date(wo.completed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Cost breakdown */}
                {wo.total_cost > 0 && (
                  <div className="flex shrink-0 items-center gap-3 border-l border-[var(--color-border-light)] bg-[var(--color-surface-tertiary)] px-5 py-4">
                    {wo.total_labor_cost > 0 && (
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                          Labor
                        </p>
                        <p className="text-xs text-[var(--text-secondary)]">
                          {formatCurrency(wo.total_labor_cost)}
                        </p>
                      </div>
                    )}
                    {wo.total_parts_cost > 0 && (
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                          Parts
                        </p>
                        <p className="text-xs text-[var(--text-secondary)]">
                          {formatCurrency(wo.total_parts_cost)}
                        </p>
                      </div>
                    )}
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                        Total
                      </p>
                      <p className="text-sm font-bold text-[var(--text-primary)]">
                        {formatCurrency(wo.total_cost)}
                      </p>
                    </div>
                  </div>
                )}
                <div
                  className="flex items-center gap-1 shrink-0 pr-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setDeleteTarget(wo.id)}
                    className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-danger)] transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Wrench />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No work orders found
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create a work order to start tracking jobs.
            </p>
            <Button
              className="mt-4"
              onClick={() => navigate("/work-orders/new")}
            >
              <Plus className="h-4 w-4" /> New Work Order
            </Button>
          </div>
        </Card>
      )}

      <ConfirmDeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        apiPath="/api/v1/work-orders"
        entityId={deleteTarget || ""}
        queryKey="work-orders"
      />

      <BulkResultPanel
        outcome={bulkOutcome}
        onClose={() => setBulkOutcome(null)}
        noun="work orders"
      />

      <BulkActionBar
        selectedCount={selected.size}
        onClearSelection={() => setSelected(new Set())}
        actions={[
          {
            label: "Cancel Selected",
            onClick: () => runBulk("work_order.cancel"),
            isLoading: bulkRun.isPending,
          },
          {
            label: "Delete Selected",
            onClick: () => runBulk("work_order.delete", { cascade: true }),
            variant: "danger",
            isLoading: bulkRun.isPending,
          },
        ]}
      />
    </div>
  );
}

export default WorkOrdersPage;
